import React, { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import { Box, Button, Typography } from '@mui/material';

import {
  CreateClientInvitationInput,
  SegmentEventType,
  useCreateClientInvitationMutation,
} from 'api';
import { useTrackingQuery } from 'app-react-query/hooks';
import { InviteInput, Layouts, Loaders, PaddedPaper } from 'components';
import { useCurrentUser, useNotify } from 'hooks';
import { getAppImage } from 'helpers';
import * as paths from 'paths';

import { clientInvitesSchema } from '../validations';

const SetupWorkspaceInvites: FC = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const { t } = useTranslation();

  const track = useTrackingQuery();
  const currentUser = useCurrentUser();

  const initialValues: CreateClientInvitationInput = {
    email_addresses: [],
  };

  const { mutate: createClientInvitations, isLoading: createClientLoading } =
    useCreateClientInvitationMutation({
      onError: notify.mutationError,
      onSuccess: () => {
        notify.info({ message: t('invites:inviteSuccess') });
        completeCreateWorkspace();
      },
    });

  function handleSubmit(values: CreateClientInvitationInput) {
    createClientInvitations({ data: values });
    track(
      SegmentEventType.SignupLoginExistingUserCreateClientClientModifiedInviteColleagues,
      currentUser.id,
      {
        client_invitations: values.email_addresses.length,
      }
    );
  }

  function completeCreateWorkspace() {
    navigate(paths.setupWorkspace, { replace: true });
  }

  function renderImage() {
    return <img width={500} height={500} {...getAppImage('illustrations/juggling.png')} />;
  }

  function renderMain() {
    return (
      <PaddedPaper elevation={0} padding="64px 48px">
        <Formik
          onSubmit={handleSubmit}
          initialValues={initialValues}
          validationSchema={clientInvitesSchema}
        >
          {({ submitForm }) => (
            <Layouts.OnboardingBox>
              <Typography align="center" paragraph variant="h2">
                {t('onboarding:clientInvitesHeader')}
              </Typography>
              <Typography align="center" paragraph color="textSecondary">
                {t('onboarding:clientInvitesSubHead')}
              </Typography>
              <Box paddingTop={4}>
                <InviteInput />
              </Box>
              <Box paddingTop={5}>
                <Button
                  fullWidth
                  onClick={submitForm}
                  size="large"
                  variant="contained"
                  color="primary"
                  disabled={createClientLoading}
                >
                  {createClientLoading ? (
                    <Loaders.Standard height={26} />
                  ) : (
                    t('onboarding:clientInvitesButton')
                  )}
                </Button>
              </Box>
              <Box paddingTop={2}>
                <Button
                  fullWidth
                  onClick={() => completeCreateWorkspace()}
                  size="large"
                  variant="contained"
                >
                  {t('onboarding:skipForNow')}
                </Button>
              </Box>
            </Layouts.OnboardingBox>
          )}
        </Formik>
      </PaddedPaper>
    );
  }

  return <Layouts.Onboarding image={renderImage()} main={renderMain()} />;
};

export default SetupWorkspaceInvites;
